import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { getExpertCalendarUrlById, getExpertNameById } from '../expertsData';
import { fetchExperts, fetchUserCfpSessions, deleteUserCfpSession, editUserCfpSession, payCfpSessionCheckout } from '../../../utils/api';
import { useContentCatalog } from '../../../utils/contentCatalog';
import CfpSegmentTabs from './CfpSegmentTabs';
import CfpTopicCard from './CfpTopicCard';
import CfpTopicCarousel from './CfpTopicCarousel';
import CfpSessionDocumentsDialog from './CfpSessionDocumentsDialog';
import { SuccessToast, useSuccessToast } from '../../shared/SuccessToast';
import {
  defaultCfpSegmentId,
  getSegmentById,
  resolveSelectedCfpTopic,
  resolveTopicIdFromTopicName,
} from './cfpSessionData';
import {
  canDeleteCfpSession,
  canEditCfpSession,
  canPayCfpSession,
  formatCfpSessionTimestamp,
  getCfpSessionDeleteDisabledReason,
  getCfpSessionEditDisabledReason,
  getSessionStatusMeta,
  isInitialCfpSession,
} from './cfpSessionStatus';
import styles from './ScheduleCfpSessionPage.module.css';

function TimestampCell({ value }) {
  const timestamp = formatCfpSessionTimestamp(value);
  if (!timestamp) {
    return <span className={styles.listEmptyCell}>—</span>;
  }
  return (
    <div className={styles.listDateInner}>
      <span className={styles.listDateText}>{timestamp.date}</span>
      {timestamp.time && <span className={styles.listDateTime}>{timestamp.time}</span>}
    </div>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg viewBox="0 0 24 24" width="44" height="44" fill="currentColor" aria-hidden="true">
      <path d="M19 3H4.99c-1.11 0-1.98.89-1.98 2L3 19c0 1.11.88 2 1.99 2H19c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2zm0 12h-4c0 1.66-1.35 3-3 3s-3-1.34-3-3H4.99V5H19v10z" />
    </svg>
  );
}

function DeleteIcon({ width = 18, height = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden="true">
      <path d="M6 7h12v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7zm3-5h6l1 1h4v2H4V3h4l1-1zm1 8h2v10h-2V10zm4 0h2v10h-2V10z" />
    </svg>
  );
}

function EditIcon({ width = 18, height = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden="true">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
    </svg>
  );
}

function CalendarIcon({ width = 18, height = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden="true">
      <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zM7 12h5v5H7v-5z" />
    </svg>
  );
}

function DocumentIcon({ width = 18, height = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2 5 5h-5V4zM8 13h8v2H8v-2zm0 4h8v2H8v-2z" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="42"
        strokeDashoffset="12"
      />
    </svg>
  );
}

function ErrorCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.89 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.42L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg className={styles.listEditDialogInfoBannerIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 10.5V16.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="7.75" r="1.1" fill="currentColor" />
    </svg>
  );
}

function FooterCheckIcon() {
  return (
    <svg className={styles.listEditDialogFooterCheckIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 8.2L7.1 10.3L11.5 5.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ActionFeedbackBanner({ feedback, onDismiss }) {
  if (!feedback || feedback.type === 'success') return null;

  const Icon = feedback.type === 'loading'
    ? SpinnerIcon
    : ErrorCircleIcon;

  return (
    <div className={styles.listActionFeedbackWrap} role="presentation">
      <div
        className={`${styles.listActionFeedback} ${styles[`listActionFeedback-${feedback.type}`]}`}
        role={feedback.type === 'loading' ? 'status' : 'alert'}
        aria-live="polite"
      >
        <span
          className={`${styles.listActionFeedbackIcon} ${feedback.type === 'loading' ? styles.listActionFeedbackIconSpin : ''}`}
          aria-hidden="true"
        >
          <Icon />
        </span>
        <p className={styles.listActionFeedbackText}>{feedback.message}</p>
        {feedback.type !== 'loading' && (
          <button
            type="button"
            className={styles.listActionFeedbackDismiss}
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

const deleteDialogEase = [0.16, 1, 0.3, 1];

const deleteDialogOverlayMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.22, ease: deleteDialogEase },
};

const deleteDialogCardMotion = {
  initial: { opacity: 0, y: 20, scale: 0.94 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 12, scale: 0.97 },
  transition: { duration: 0.32, ease: deleteDialogEase },
};

const deleteDialogContentMotion = {
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: {
    hidden: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
    visible: { transition: { staggerChildren: 0.07, delayChildren: 0.06 } },
  },
};

const deleteDialogItemMotion = {
  variants: {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.28, ease: deleteDialogEase },
    },
  },
};

function DeleteConfirmDialog({ session, isDeleting, onCancel, onConfirm }) {
  if (typeof document === 'undefined') return null;

  const topic = String(session?.topic ?? '').trim() || '—';

  return createPortal(
    <AnimatePresence mode="wait">
      {session && (
        <motion.div
          key="cfp-delete-overlay"
          className={styles.listDeleteOverlay}
          role="presentation"
          onClick={isDeleting ? undefined : onCancel}
          {...deleteDialogOverlayMotion}
        >
          <motion.div
            className={styles.listDeleteDialog}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cfp-delete-dialog-title"
            aria-describedby="cfp-delete-dialog-desc"
            onClick={(e) => e.stopPropagation()}
            {...deleteDialogCardMotion}
          >
            <button
              type="button"
              className={styles.listDeleteDialogClose}
              onClick={onCancel}
              disabled={isDeleting}
              aria-label="Close dialog"
            >
              <CloseIcon />
            </button>

            <motion.div className={styles.listDeleteDialogBody} {...deleteDialogContentMotion}>
              <motion.div className={styles.listDeleteDialogHeader} {...deleteDialogItemMotion}>
                <div className={styles.listDeleteDialogIconWrap} aria-hidden="true">
                  <span className={styles.listDeleteDialogIconRing} />
                  <WarningIcon />
                </div>
                <h2 id="cfp-delete-dialog-title" className={styles.listDeleteDialogTitle}>
                  Delete Session?
                </h2>
                <p id="cfp-delete-dialog-desc" className={styles.listDeleteDialogText}>
                  This session will be removed and cannot be recovered.
                </p>
              </motion.div>

              <motion.div className={styles.listDeleteDialogSessionInfo} {...deleteDialogItemMotion}>
                <div className={styles.listDeleteDialogSessionRow}>
                  <span className={styles.listDeleteDialogSessionLabel}>Topic</span>
                  <span className={styles.listDeleteDialogSessionValue} title={topic}>
                    {topic}
                  </span>
                </div>
                {session.expertName && (
                  <div className={styles.listDeleteDialogSessionRow}>
                    <span className={styles.listDeleteDialogSessionLabel}>Expert</span>
                    <span className={styles.listDeleteDialogSessionValue} title={session.expertName}>
                      {session.expertName}
                    </span>
                  </div>
                )}
              </motion.div>

              <motion.div className={styles.listDeleteDialogActions} {...deleteDialogItemMotion}>
                <button
                  type="button"
                  className={styles.listDeleteDialogCancel}
                  onClick={onCancel}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.listDeleteDialogConfirm}
                  onClick={onConfirm}
                  disabled={isDeleting}
                  aria-busy={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className={styles.listDeleteDialogConfirmSpinner} aria-hidden="true">
                        <SpinnerIcon />
                      </span>
                      Deleting…
                    </>
                  ) : (
                    <>
                      <DeleteIcon width={15} height={15} />
                      Delete
                    </>
                  )}
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function EditTopicDialog({
  session,
  segments,
  maxSelections,
  selectionPrompt,
  selectionNote,
  activeSegmentId,
  selectedSegmentId,
  selectedTopicIds,
  isUpdating,
  onCancel,
  onConfirm,
  onSegmentChange,
  onTopicToggle,
  onTopicSelect,
}) {
  if (typeof document === 'undefined') return null;

  const activeSegment = getSegmentById(segments, activeSegmentId);
  const selectedCount = selectedTopicIds.length;
  const atMaxSelections = selectedCount >= maxSelections;
  const selectedTopic = resolveSelectedCfpTopic(segments, selectedTopicIds, selectedSegmentId);
  const isTopicSelected = (topicId) =>
    selectedSegmentId === activeSegmentId && selectedTopicIds.includes(topicId);
  const currentTopicName = String(session?.topic ?? '').trim();
  const hasSelection = selectedTopicIds.length > 0;
  const topicChanged = selectedTopic?.topic !== currentTopicName;
  const canUpdate = hasSelection && topicChanged && !isUpdating;
  const selectedTopicTitle = selectedTopic?.topic ?? null;

  return createPortal(
    <AnimatePresence mode="wait">
      {session && (
        <motion.div
          key="cfp-edit-overlay"
          className={styles.listEditOverlay}
          role="presentation"
          onClick={isUpdating ? undefined : onCancel}
          {...deleteDialogOverlayMotion}
        >
          <motion.div
            className={styles.listEditDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfp-edit-dialog-title"
            aria-describedby="cfp-edit-dialog-desc"
            onClick={(e) => e.stopPropagation()}
            {...deleteDialogCardMotion}
          >
            <button
              type="button"
              className={styles.listEditDialogClose}
              onClick={onCancel}
              disabled={isUpdating}
              aria-label="Close dialog"
            >
              <CloseIcon />
            </button>

            <motion.div className={styles.listEditDialogBody} {...deleteDialogContentMotion}>
              <motion.div className={styles.listEditDialogHeader} {...deleteDialogItemMotion}>
                <div className={styles.listEditDialogHeaderTop}>
                  <div className={styles.listEditDialogIconWrap} aria-hidden="true">
                    <EditIcon width={20} height={20} />
                  </div>
                  <div className={styles.listEditDialogHeaderText}>
                    <h2 id="cfp-edit-dialog-title" className={styles.listEditDialogTitle}>
                      Edit Session Topic
                    </h2>
                    <p id="cfp-edit-dialog-desc" className={styles.listEditDialogText}>
                      Your current topic is pre-selected. Choose a different topic below, then save your
                      changes.
                    </p>
                  </div>
                </div>
                {currentTopicName && (
                  <div className={styles.listEditDialogCurrentTopic}>
                    <span className={styles.listEditDialogCurrentTopicLabel}>Current topic</span>
                    <span className={styles.listEditDialogCurrentTopicValue} title={currentTopicName}>
                      {currentTopicName}
                    </span>
                  </div>
                )}
              </motion.div>

              <motion.div className={styles.listEditDialogContent} {...deleteDialogItemMotion}>
                <div className={styles.listEditDialogInfoBanner} role="note">
                  <InfoIcon />
                  <div className={styles.listEditDialogInfoBannerContent}>
                    <p className={styles.listEditDialogInfoBannerText}>
                      {selectionPrompt}
                    </p>
                    <p className={styles.listEditDialogInfoBannerSubtext}>
                      {selectionNote}
                    </p>
                  </div>
                </div>

                <div className={styles.listEditDialogTabsWrap}>
                  <CfpSegmentTabs
                    segments={segments}
                    activeSegmentId={activeSegmentId}
                    onSegmentChange={onSegmentChange}
                  />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSegmentId}
                    id={`cfp-edit-tabpanel-${activeSegmentId}`}
                    role="tabpanel"
                    aria-labelledby={`cfp-tab-${activeSegmentId}`}
                    className={styles.listEditDialogCarouselWrap}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <CfpTopicCarousel segmentId={activeSegmentId}>
                      {(activeSegment?.topics || []).map((topic, index) => (
                        <CfpTopicCard
                          key={topic.id}
                          topic={topic}
                          index={index}
                          selected={isTopicSelected(topic.id)}
                          disabled={atMaxSelections && !isTopicSelected(topic.id)}
                          onToggle={onTopicToggle}
                          onSelect={onTopicSelect}
                        />
                      ))}
                    </CfpTopicCarousel>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </motion.div>

            <div className={styles.listEditDialogFooter}>
              <div className={styles.listEditDialogFooterLeft}>
                <AnimatePresence initial={false}>
                  {selectedTopicTitle && (
                    <motion.div
                      key="edit-selected-topic"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                      className={styles.listEditDialogFooterSelected}
                    >
                      <span className={styles.listEditDialogFooterSelectedLabel}>Selected:</span>
                      <span className={styles.listEditDialogFooterSelectedTitle} title={selectedTopicTitle}>
                        {selectedTopicTitle}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence mode="wait" initial={false}>
                  {canUpdate ? (
                    <motion.div
                      key="ready"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      className={styles.listEditDialogFooterHelper}
                    >
                      <FooterCheckIcon />
                      <span>Ready to update your session</span>
                    </motion.div>
                  ) : topicChanged === false && hasSelection ? (
                    <motion.div
                      key="unchanged"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      className={styles.listEditDialogFooterHelper}
                    >
                      <span>Choose a different topic to enable update</span>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="prompt"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      className={styles.listEditDialogFooterHelper}
                    >
                      <span>Select 1 topic to personalize your session</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className={styles.listEditDialogFooterActions}>
                <button
                  type="button"
                  className={styles.listEditDialogCancel}
                  onClick={onCancel}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.listEditDialogUpdate} ${canUpdate ? styles.listEditDialogUpdateReady : ''}`}
                  onClick={onConfirm}
                  disabled={!canUpdate}
                  aria-busy={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <span className={styles.listEditDialogUpdateSpinner} aria-hidden="true">
                        <SpinnerIcon />
                      </span>
                      Updating…
                    </>
                  ) : (
                    'Update Topic'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function CfpSessionListView({ navigate, onAddNew }) {
  const { catalog } = useContentCatalog();
  const statusCopy = catalog?.cfp_session_statuses;
  const statusFilters = statusCopy?.filters || [];
  const segments = useMemo(
    () => catalog?.cfp_session_topics?.segments || [],
    [catalog?.cfp_session_topics?.segments],
  );
  const maxSelections = Number(catalog?.cfp_session_topics?.max_selections) || 1;
  const selectionPrompt = catalog?.cfp_session_topics?.selection_prompt || '';
  const selectionNote = catalog?.cfp_session_topics?.selection_note || '';
  const fallbackSegmentId = defaultCfpSegmentId(segments);

  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [experts, setExperts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [editSession, setEditSession] = useState(null);
  const [editActiveSegmentId, setEditActiveSegmentId] = useState('');
  const [editSelectedSegmentId, setEditSelectedSegmentId] = useState('');
  const [editSelectedTopicIds, setEditSelectedTopicIds] = useState([]);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [documentsSession, setDocumentsSession] = useState(null);
  const { message: successToastMessage, isExiting: successToastExiting, showToast: showSuccessToast } = useSuccessToast();

  const loadSessions = useCallback(async ({ silent = false, signal = null } = {}) => {
    if (!silent) setLoading(true);
    const [result, expertsData] = await Promise.all([
      fetchUserCfpSessions(navigate, signal),
      fetchExperts(signal),
    ]);
    if (signal?.aborted) return;
    if (expertsData) setExperts(expertsData);
    if (result.ok) {
      setSessions(Array.isArray(result.sessions) ? result.sessions : []);
    }
    if (!silent) setLoading(false);
  }, [navigate]);

  const dismissActionFeedback = useCallback(() => {
    setActionFeedback(null);
  }, []);

  const openDeleteConfirm = useCallback((row, expertName, topicName) => {
    if (deletingId || !canDeleteCfpSession(row)) return;
    setDeleteConfirmSession({
      id: row?.id,
      topic: topicName,
      expertName,
    });
  }, [deletingId]);

  const closeDeleteConfirm = useCallback(() => {
    if (deletingId) return;
    setDeleteConfirmSession(null);
  }, [deletingId]);

  const openEditDialog = useCallback((row, topicName) => {
    if (updatingId || deletingId || !canEditCfpSession(row)) return;

    const resolved = resolveTopicIdFromTopicName(segments, topicName);
    const segmentId = resolved?.segmentId ?? fallbackSegmentId;
    setEditActiveSegmentId(segmentId);
    setEditSelectedSegmentId(segmentId);
    setEditSelectedTopicIds(resolved?.topicId ? [resolved.topicId] : []);
    setEditSession({
      id: row?.id,
      topic: topicName,
    });
  }, [deletingId, fallbackSegmentId, segments, updatingId]);

  const closeEditDialog = useCallback(() => {
    if (updatingId) return;
    setEditSession(null);
    setEditSelectedTopicIds([]);
    setEditActiveSegmentId(fallbackSegmentId);
    setEditSelectedSegmentId(fallbackSegmentId);
  }, [fallbackSegmentId, updatingId]);

  const openDocumentsDialog = useCallback((row, topicName) => {
    if (deletingId || updatingId || payingId) return;
    setDocumentsSession({
      id: row?.id,
      topic: topicName,
    });
  }, [deletingId, payingId, updatingId]);

  const closeDocumentsDialog = useCallback(() => {
    setDocumentsSession(null);
  }, []);

  const handleEditSegmentChange = useCallback((segmentId) => {
    setEditActiveSegmentId(segmentId);
  }, []);

  const handleEditTopicToggle = useCallback((topicId) => {
    setEditSelectedSegmentId(editActiveSegmentId);
    setEditSelectedTopicIds((prev) => {
      if (prev.includes(topicId)) return [];
      return [topicId];
    });
  }, [editActiveSegmentId]);

  const handleEditTopicSelect = useCallback((topicId) => {
    setEditSelectedSegmentId(editActiveSegmentId);
    setEditSelectedTopicIds([topicId]);
  }, [editActiveSegmentId]);

  const editSelectedTopic = useMemo(
    () => resolveSelectedCfpTopic(segments, editSelectedTopicIds, editSelectedSegmentId),
    [segments, editSelectedTopicIds, editSelectedSegmentId],
  );

  const handleUpdateSession = useCallback(
    async () => {
      const sessionId = editSession?.id;
      if (!sessionId || updatingId || editSelectedTopicIds.length === 0) return;

      setUpdatingId(sessionId);
      setActionFeedback({ type: 'loading', message: 'Updating session topic…' });

      const result = await editUserCfpSession(
        {
          id: sessionId,
          tabLabel: editSelectedTopic?.tabLabel,
          topicCardTitle: editSelectedTopic?.topicCardTitle,
        },
        navigate,
      );

      setEditSession(null);
      setEditSelectedTopicIds([]);
      setEditActiveSegmentId(fallbackSegmentId);
      setEditSelectedSegmentId(fallbackSegmentId);

      if (result.ok) {
        await loadSessions({ silent: true });
        setActionFeedback(null);
        showSuccessToast(result.message || 'Session topic updated successfully.');
      } else {
        setActionFeedback({
          type: 'error',
          message: result.message || 'Failed to update session topic.',
        });
      }

      setUpdatingId(null);
    },
    [editSession, editSelectedTopic, editSelectedTopicIds, fallbackSegmentId, loadSessions, navigate, showSuccessToast, updatingId],
  );

  const handleDeleteSession = useCallback(
    async () => {
      const sessionId = deleteConfirmSession?.id;
      if (!sessionId || deletingId) return;

      setDeletingId(sessionId);
      setActionFeedback({ type: 'loading', message: 'Deleting session…' });

      const result = await deleteUserCfpSession({ id: sessionId }, navigate);

      setDeleteConfirmSession(null);

      if (result.ok) {
        await loadSessions({ silent: true });
        setActionFeedback(null);
        showSuccessToast(result.message || 'Session deleted successfully.');
      } else {
        setActionFeedback({
          type: 'error',
          message: result.message || 'Failed to delete session.',
        });
      }

      setDeletingId(null);
    },
    [deleteConfirmSession, deletingId, loadSessions, navigate, showSuccessToast],
  );

  const handlePaySession = useCallback(
    async (row) => {
      const sessionId = row?.id;
      if (!sessionId || payingId || !canPayCfpSession(row)) return;

      setPayingId(sessionId);
      setActionFeedback({ type: 'loading', message: 'Redirecting to checkout…' });

      const result = await payCfpSessionCheckout({ id: sessionId }, navigate);

      if (result.ok && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }

      setPayingId(null);
      setActionFeedback({
        type: 'error',
        message: result.message || 'Unable to start checkout. Please try again.',
      });
    },
    [navigate, payingId],
  );

  const handleOpenCalendar = useCallback(
    (row) => {
      if (!isInitialCfpSession(row)) return;

      const expertId = String(row?.expert_id ?? '').trim();
      const calendarUrl = getExpertCalendarUrlById(expertId, experts);
      if (!calendarUrl) {
        setActionFeedback({
          type: 'error',
          message: 'Calendar link is unavailable for this expert.',
        });
        return;
      }

      window.open(calendarUrl, '_blank', 'noopener,noreferrer');
    },
    [experts],
  );

  useEffect(() => {
    if (!deleteConfirmSession) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !deletingId) closeDeleteConfirm();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeDeleteConfirm, deleteConfirmSession, deletingId]);

  useEffect(() => {
    if (!editSession) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !updatingId) closeEditDialog();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeEditDialog, editSession, updatingId]);

  useEffect(() => {
    const controller = new AbortController();
    loadSessions({ signal: controller.signal });
    return () => controller.abort();
  }, [loadSessions]);

  const filteredSessions = useMemo(() => {
    if (statusFilter === 'all') return sessions;
    return sessions.filter((row) => getSessionStatusMeta(row, statusCopy).filterKey === statusFilter);
  }, [sessions, statusCopy, statusFilter]);

  return (
    <main className={styles.main}>
      <SuccessToast message={successToastMessage} isExiting={successToastExiting} />
      <div className={styles.container}>

        {/* ── Intro ──────────────────────────────────────────────── */}
        <div className={styles.listIntro}>
          <section className={styles.listIntroCard} aria-label="CFP Session Overview">
            <div className={styles.listIntroAccent} aria-hidden="true" />
            <div className={styles.listIntroBody}>
              <p className={styles.listIntroText}>
                <strong>
                  Your plan includes a dedicated strategy session with a Certified Financial Planner
                </strong>{' '}
                to navigate the financial side of your career &amp; life transition—focusing on your top
                financial priority.
              </p>
              <p className={styles.listIntroText}>
                As your financial journey evolves, you can unlock advanced, deep-dive sessions tailored to
                your more complex milestones with transparent, on-demand pricing whenever you&apos;re ready.
              </p>
              <p className={`${styles.listIntroText} ${styles.listIntroTextNote}`}>
                <em>
                  <strong>Coming soon:</strong> We are working on adding HSA payment capability in the near
                  future, allowing you to seamlessly fund your advanced sessions using your tax-advantaged
                  dollars.
                </em>
              </p>
            </div>
          </section>
        </div>

        {/* ── Session table ──────────────────────────────────────── */}
        <section className={styles.listSection}>
          <div className={styles.listToolbar}>
            <div className={styles.listToolbarLeft}>
              <label className={styles.listFilterLabel} htmlFor="cfp-status-filter">
                Filter
              </label>
              <select
                id="cfp-status-filter"
                className={styles.listStatusSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                {statusFilters.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className={styles.listAddButton} onClick={onAddNew}>
              <PlusIcon />
              Add New Session
            </button>
          </div>

          <ActionFeedbackBanner feedback={actionFeedback} onDismiss={dismissActionFeedback} />

          {loading ? (
            <div className={styles.listLoadingShimmer} aria-busy="true" aria-live="polite">
              {[0, 1, 2].map((n) => (
                <div
                  key={n}
                  className={styles.listShimmerRow}
                  style={{ opacity: 1 - n * 0.22 }}
                  aria-hidden="true"
                />
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className={styles.listEmptyState} role="status">
              <div className={styles.listEmptyIconWrap}>
                <InboxIcon />
              </div>
              <p className={styles.listEmptyTitle}>No sessions found</p>
              <p className={styles.listEmptySubtitle}>
                {statusFilter === 'all'
                  ? 'Schedule your first CFP session to get started.'
                  : 'Try a different status filter or add a new session.'}
              </p>
              {statusFilter === 'all' && (
                <button type="button" className={styles.listEmptyCta} onClick={onAddNew}>
                  <PlusIcon />
                  Schedule Your First Session
                </button>
              )}
            </div>
          ) : (
            <div className={styles.listTableWrapper}>
              <table className={styles.listTable}>
                <colgroup>
                  <col className={styles.listColExpert} />
                  <col className={styles.listColTopic} />
                  <col className={styles.listColStatus} />
                  <col className={styles.listColDate} />
                  <col className={styles.listColSessionDate} />
                  <col className={styles.listColAction} />
                  <col className={styles.listColDocument} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Expert</th>
                    <th scope="col">Topic</th>
                    <th scope="col" className={styles.listCol}>Status</th>
                    <th scope="col" className={styles.listCol}>Created</th>
                    <th scope="col" className={styles.listCol}>Session Date</th>
                    <th scope="col" className={`${styles.listCol} ${styles.listColAction}`}>Action</th>
                    <th scope="col" className={`${styles.listCol} ${styles.listColDocument}`}>Document</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.map((row, index) => {
                    const expertId = String(row?.expert_id ?? '').trim();
                    const expertName = getExpertNameById(expertId, experts) || expertId || '—';
                    const topicName = String(row?.topic ?? '').trim() || '—';
                    const statusMeta = getSessionStatusMeta(row, statusCopy);
                    const key = row?.id ?? `${topicName}-${index}`;
                    const canDelete = canDeleteCfpSession(row);
                    const canEdit = canEditCfpSession(row);
                    const canPay = canPayCfpSession(row);
                    const isInitialSession = isInitialCfpSession(row);
                    const calendarUrl = isInitialSession
                      ? getExpertCalendarUrlById(expertId, experts)
                      : null;
                    const canOpenCalendar = isInitialSession && Boolean(calendarUrl);
                    const deleteDisabledReason = getCfpSessionDeleteDisabledReason(row, statusCopy);
                    const editDisabledReason = getCfpSessionEditDisabledReason(row, statusCopy);
                    const calendarDisabledReason = isInitialSession && !calendarUrl
                      ? 'Calendar link is unavailable for this expert'
                      : null;
                    const isDeleteDisabled = !canDelete || Boolean(deletingId) || Boolean(updatingId) || Boolean(payingId);
                    const isEditDisabled = !canEdit || Boolean(deletingId) || Boolean(updatingId) || Boolean(payingId);
                    const isPayDisabled = !canPay || Boolean(deletingId) || Boolean(updatingId) || Boolean(payingId);
                    const isCalendarDisabled = !canOpenCalendar || Boolean(deletingId) || Boolean(updatingId) || Boolean(payingId);
                    const isPaying = payingId === row?.id;

                    return (
                      <tr
                        key={key}
                        className={styles.listTableRow}
                        style={{ animationDelay: `${index * 45}ms` }}
                      >
                        <td data-label="Expert">
                          <span className={styles.listExpertTag}>{expertName}</span>
                        </td>
                        <td data-label="Topic">
                          <span className={styles.listTopicTag}>{topicName}</span>
                        </td>
                        <td data-label="Status" className={styles.listCol}>
                          <span
                            className={`${styles.listStatusTag} ${styles[`listStatusTag-${statusMeta.tone}`]}`}
                          >
                            {statusMeta.label}
                          </span>
                        </td>
                        <td data-label="Created" className={styles.listCol}>
                          <TimestampCell value={row?.created_at} />
                        </td>
                        <td data-label="Session Date" className={styles.listCol}>
                          <TimestampCell value={row?.schedule_at} />
                        </td>
                        <td data-label="Action" className={`${styles.listCol} ${styles.listColAction}`}>
                          <div className={styles.listActionCell}>
                            {canPay && (
                              <button
                                type="button"
                                className={styles.listPayButton}
                                onClick={() => handlePaySession(row)}
                                disabled={isPayDisabled}
                                title={isPaying ? 'Redirecting to checkout…' : 'Pay for session'}
                                aria-label={isPaying ? 'Redirecting to checkout…' : 'Pay for session'}
                                aria-busy={isPaying}
                              >
                                {isPaying ? '…' : 'Pay'}
                              </button>
                            )}
                            {isInitialSession && (
                              <button
                                type="button"
                                className={`${styles.listCalendarButton} ${!canOpenCalendar ? styles.listCalendarButtonLocked : ''}`}
                                onClick={() => handleOpenCalendar(row)}
                                disabled={isCalendarDisabled}
                                title={calendarDisabledReason || 'Schedule session on Calendly'}
                                aria-label={calendarDisabledReason || 'Schedule session on Calendly'}
                              >
                                <CalendarIcon />
                              </button>
                            )}
                            <button
                              type="button"
                              className={`${styles.listEditButton} ${!canEdit ? styles.listEditButtonLocked : ''}`}
                              onClick={() => {
                                if (!canEdit) return;
                                openEditDialog(row, topicName);
                              }}
                              disabled={isEditDisabled}
                              title={editDisabledReason || 'Edit session topic'}
                              aria-label={editDisabledReason || 'Edit session topic'}
                            >
                              <EditIcon />
                            </button>
                            <button
                              type="button"
                              className={`${styles.listDeleteButton} ${!canDelete ? styles.listDeleteButtonLocked : ''}`}
                              onClick={() => {
                                if (!canDelete) return;
                                openDeleteConfirm(row, expertName, topicName);
                              }}
                              disabled={isDeleteDisabled}
                              title={deleteDisabledReason || 'Delete session'}
                              aria-label={deleteDisabledReason || 'Delete session'}
                            >
                              <DeleteIcon />
                            </button>
                          </div>
                        </td>
                        <td data-label="Document" className={`${styles.listCol} ${styles.listColDocument}`}>
                          <div className={styles.listDocumentCell}>
                            <button
                              type="button"
                              className={styles.listDocumentButton}
                              onClick={() => openDocumentsDialog(row, topicName)}
                              disabled={Boolean(deletingId) || Boolean(updatingId) || Boolean(payingId)}
                              title="Manage session documents"
                              aria-label="Manage session documents"
                            >
                              <DocumentIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <DeleteConfirmDialog
          session={deleteConfirmSession}
          isDeleting={Boolean(deletingId)}
          onCancel={closeDeleteConfirm}
          onConfirm={handleDeleteSession}
        />

        <EditTopicDialog
          session={editSession}
          segments={segments}
          maxSelections={maxSelections}
          selectionPrompt={selectionPrompt}
          selectionNote={selectionNote}
          activeSegmentId={editActiveSegmentId}
          selectedSegmentId={editSelectedSegmentId}
          selectedTopicIds={editSelectedTopicIds}
          isUpdating={Boolean(updatingId)}
          onCancel={closeEditDialog}
          onConfirm={handleUpdateSession}
          onSegmentChange={handleEditSegmentChange}
          onTopicToggle={handleEditTopicToggle}
          onTopicSelect={handleEditTopicSelect}
        />

        <CfpSessionDocumentsDialog
          session={documentsSession}
          navigate={navigate}
          isBusy={Boolean(deletingId) || Boolean(updatingId) || Boolean(payingId)}
          onClose={closeDocumentsDialog}
        />
      </div>
    </main>
  );
}

export default CfpSessionListView;
